import { mergeClasses } from "@fluentui/react-components";
import React from "react";
import {
  SelectionBarMarginTopDefault,
  SelectionBarMarginTopWide,
} from "./Constants";
import { useSelectionBarStyles } from "./TileSelectionBar.styles";
import {
  TileSelectionBarProps,
  TileSelectionBarRef,
} from "./TileSelectionBar.types";
/**
 * Tile Selection Bar React functional component.
 *
 */
export const TileSelectionBarRender: React.ForwardRefRenderFunction<
  TileSelectionBarRef,
  TileSelectionBarProps
> = ({ id, animate, wide, color, anchored, initialVisibility }, ref) => {
  const barRef = React.useRef<HTMLDivElement>(null);
  const [barColor, setBarColor] = React.useState(color);
  const [position, setBarPosition] = React.useState(0);
  const [visible, setVisible] = React.useState(initialVisibility);
  const styles = useSelectionBarStyles();
  const barStyles = React.useMemo(() => {
    return mergeClasses(
      styles.root,
      styles.defaultColor,
      animate && styles.animated,
      wide && styles.wide,
      anchored && styles.anchored
    );
  }, [
    anchored,
    animate,
    styles.anchored,
    styles.animated,
    styles.defaultColor,
    styles.root,
    styles.wide,
    wide,
  ]);
  /**
     Effect to update selection bar color
     */
  React.useEffect(() => {
    if (!barRef.current) {
      return;
    }
    barRef.current.style.backgroundColor = barColor || "";
  }, [barColor]);
  /**
   * Effect to update selection bar visibility
   */
  React.useEffect(() => {
    if (!barRef.current) {
      return;
    }
    barRef.current.style.visibility = visible ? "visible" : "hidden";
  }, [visible]);
  /**
   * Effect to update selection bar position
   */
  React.useLayoutEffect(() => {
    if (anchored || !barRef.current) {
      return;
    }
    const prevTop = barRef.current.offsetTop;
    const newTop = position;
    const selectionBarMarginTop = wide
      ? SelectionBarMarginTopWide
      : SelectionBarMarginTopDefault;
    barRef.current.style.transform = `translate(0px, ${selectionBarMarginTop + (newTop - prevTop)}px)`;
  }, [anchored, position, wide]);
  React.useImperativeHandle(
    ref,
    () => ({
      setColor: setBarColor,
      setPosition: setBarPosition,
      setVisible: setVisible,
    }),
    []
  );
  return <div id={id} ref={barRef} className={barStyles} />;
};
TileSelectionBarRender.displayName = "TileSelectionBar";
export const TileSelectionBar = React.forwardRef(TileSelectionBarRender);
